import { useEffect } from "react"
import { useAuthStore } from "../../../stores/auth-store"
import { getLoginUrl } from "../../../lib/auth"
import { ADMIN_PRIVILEGES } from "../../../lib/privileges"

interface RequireAuthProps {
  children: React.ReactNode
}

/**
 * Wrapper component that protects routes from unauthenticated access.
 *
 * Behavior:
 * - Shows loading state while `isInitialized` is false (auth check in progress)
 * - Redirects to login page if user is not authenticated after init completes
 * - Shows unauthorized message if user lacks VIEW.CAN_VIEW_ADMIN_DASHBOARD privilege
 * - Renders children if user is authenticated and has admin access
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const isInitialized = useAuthStore((state) => state.isInitialized)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)

  useEffect(() => {
    // Only redirect after initialization is complete and user is not authenticated
    if (isInitialized && !isAuthenticated) {
      window.location.href = getLoginUrl()
    }
  }, [isInitialized, isAuthenticated])

  // Show loading while checking authentication
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  // If not authenticated, don't render anything (redirect is happening)
  if (!isAuthenticated) {
    return null
  }

  // Check for admin dashboard privilege
  if (!user?.privileges?.includes(ADMIN_PRIVILEGES.CAN_VIEW)) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <h1 className="text-2xl font-semibold text-foreground">Unauthorized</h1>
        <p className="text-muted-foreground mt-2">You do not have permission to access the admin dashboard.</p>
      </div>
    )
  }

  // User is authenticated and has admin access, render children
  return <>{children}</>
}
